import { Button } from '@/components/ui/shadcn/button'
import { Slider } from '@/components/ui/shadcn/slider'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Info, CheckCircle2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { useState, useContext, useEffect } from 'react'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import { fetcher } from '@/lib/api/fetch'

export default function Simulations() {
  const authContext = useContext(AuthContext)
  const [reductionFactor, setReductionFactor] = useState(0.8)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (authContext.user.organization?.riskQuantificationSettings) {
      const factor =
        authContext.user.organization.riskQuantificationSettings
          .mitigationReductionFactor || 0.8
      setReductionFactor(factor)
    }
  }, [authContext.user.organization])

  const saveSettings = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await fetcher(
        `/api/admin/organization/${authContext.user.organization.id}/settings/risk-quantification`,
        {
          method: 'PUT',
          body: JSON.stringify({
            mitigationReductionFactor: reductionFactor,
          }),
        },
        authContext,
      )

      const data = await res.json()
      if (res.status !== 200) {
        setError(data.error || 'Failed to save settings')
        setLoading(false)
        return
      }

      authContext.setuser(
        {
          ...authContext.user,
          organization: {
            ...authContext.user.organization,
            riskQuantificationSettings: {
              mitigationReductionFactor: reductionFactor,
            },
          },
        },
        () => {},
      )

      setSaved(true)
      setTimeout(() => {
        setSaved(false)
      }, 5000)
    } catch {
      setError('Failed to save settings')
    } finally {
      setLoading(false)
    }
  }

  const percent = Math.round(reductionFactor * 100)

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="grid gap-4">
        <div>
          <h6 className="text-base font-semibold">Exercise Settings</h6>
          <p className="text-sm text-muted-foreground">
            Configure how exercises calculate risk quantification metrics.
          </p>
        </div>

        <ErrorAlerts error={error} />

        {saved && (
          <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
            <CheckCircle2 className="size-4" />
            <AlertDescription>
              Exercise settings saved successfully
            </AlertDescription>
          </Alert>
        )}

        <div>
          <p className="text-sm font-medium mb-1">
            Risk Mitigation Reduction Factor
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            When a risk is mitigated during an exercise, this factor determines
            how much of the risk&apos;s estimated impact is reduced. For
            example, 80% means that successfully mitigating a risk reduces its
            impact by 80%, leaving 20% residual risk.
          </p>

          <div className="px-2 pt-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Current
              </span>
              <span className="text-sm font-medium">{percent}%</span>
            </div>
            <Slider
              aria-label="Risk mitigation reduction factor"
              value={[reductionFactor]}
              onValueChange={v => setReductionFactor(v[0])}
              min={0.7}
              max={0.9}
              step={0.01}
              disabled={loading}
            />
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>70%</span>
              <span>80% (Default)</span>
              <span>90%</span>
            </div>
          </div>
        </div>

        <Alert>
          <Info className="size-4" />
          <AlertDescription>
            <strong>Note:</strong> This setting affects how risk quantification
            is calculated in future exercise sessions. Existing after action
            reports are not affected.
          </AlertDescription>
        </Alert>

        <div>
          <Button onClick={saveSettings} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  )
}
